/**
 * Twenty Faces — the questions, and what makes each answer true.
 *
 * ── The one rule the whole game rests on ─────────────────────────────────────
 *
 * A board like this is only winnable if no two people on it answer every
 * question the same way. If they do, the player can ask everything and still be
 * left with two faces and a coin toss, which reads as the game cheating.
 *
 * That is not a hypothetical here. Several of the Twelve are barely more than a
 * name in a list — characters.js says so itself — and Philip, Bartholomew,
 * Thaddaeus and Simon the Zealot are close to indistinguishable on scripture
 * alone. On the first pass all four collapsed into two pairs.
 *
 * The emblem questions are what separate them, and that is the right place to
 * look: the emblem is on the card, every player can see it, and Guess Who has
 * always been a game about the pictures. `scripts/check-faces.mjs` re-runs the
 * check and fails loudly if a future edit reintroduces a collision.
 *
 * ── Two kinds of question, kept apart on purpose ─────────────────────────────
 *
 * `look` questions are about the card in front of you. They are the game.
 * `life` questions are about the person, and they are the reason the game is
 * here: every one of them carries a reference, so eight or nine rounds teaches
 * who fished, who prophesied, who served a foreign king, and who was killed.
 *
 * ── On the portraits ─────────────────────────────────────────────────────────
 *
 * The look questions only ever ask about things CharacterPortrait actually
 * draws in a way the player can see. Hair colour is deliberately not asked
 * about: the drawing hides the hair under a kaffiyeh, turban, hood or veil, so
 * for eleven of the thirty the honest answer would be "you cannot tell".
 *
 * ── On the scripture ─────────────────────────────────────────────────────────
 *
 * Every `life` question gives its references and says what is there in the
 * app's own words. Nothing is set in quotation marks, because there is no
 * scripture text in this file to quote — putting a paraphrase inside quote
 * marks would be presenting my sentence as the Bible's.
 */

import { CHARACTERS } from './characters.js';

/** Twenty faces, dealt from thirty. Four across and five down fits a phone. */
export const BOARD_SIZE = 20;

const ids = (...list) => new Set(list);

const TWELVE = new Set(
  CHARACTERS.filter((c) => c.group === 'The Twelve').map((c) => c.id),
);

const WOMEN = ids(
  'mary-mother-of-jesus',
  'mary-magdalene',
  'priscilla',
  'lydia',
  'ruth',
  'esther',
  'deborah',
);

const FISHERMEN = ids('peter', 'andrew', 'james-son-of-zebedee', 'john');

/*
 * Everyone scripture calls a prophet *by name*, which is a wider list than the
 * four so-called major ones.
 *
 * David is on it because Peter puts him there at Pentecost, and leaving him off
 * would have taught the wrong thing to anyone who knew that verse. Paul and
 * Barnabas are not: Acts 13:1 calls the five men at Antioch "prophets and
 * teachers" as a group and never says which were which, so a yes here would be
 * as arguable as a no, and a question with an arguable answer has no place in
 * a game that eliminates faces on it.
 */
const PROPHETS = ids(
  'abraham',
  'moses',
  'david',
  'elijah',
  'isaiah',
  'daniel',
  'deborah',
  'john-the-baptist',
);

const CROWNED = ids('david', 'esther');

const FOREIGN_COURT = ids('joseph', 'daniel', 'esther');

const SECOND_NAME = ids(
  'peter',
  'thomas',
  'paul',
  'barnabas',
  'abraham',
  'esther',
  'daniel',
  'joseph',
);

const PUT_TO_DEATH = ids('john-the-baptist', 'stephen', 'james-son-of-zebedee');

/** Everyone the Gospels put in the same room as Jesus before the resurrection. */
const MET_JESUS = new Set([
  ...TWELVE,
  'mary-mother-of-jesus',
  'mary-magdalene',
  'john-the-baptist',
]);

const emblemIn = (...names) => (c) => names.includes(c.portrait.emblem);

/**
 * Names as they read on a card four across on a phone.
 *
 * Only the four that do not fit. Mary the mother of Jesus becomes Mary of
 * Nazareth — her own second name in characters.js — rather than something
 * shortened into inaccuracy, and the two Jameses keep the father who is the
 * only thing scripture uses to tell them apart.
 */
const BOARD_NAMES = {
  'james-son-of-zebedee': 'James (Zebedee)',
  'james-son-of-alphaeus': 'James (Alphaeus)',
  'mary-mother-of-jesus': 'Mary of Nazareth',
  joseph: 'Joseph',
};

export const boardName = (c) => BOARD_NAMES[c.id] ?? c.name;

/**
 * The question bank.
 *
 * `yes` and `no` are written as an opponent would say them out loud, because
 * the answer strip is the only voice in the game. `note` and `ref` appear
 * under the answer on the `life` questions — that is where the teaching goes,
 * and it arrives at the moment the player has just used the fact.
 */
export const FACE_QUESTIONS = [
  /* ── the card in front of you ─────────────────────────────────────────── */
  {
    id: 'beard',
    group: 'look',
    ask: 'Do they have a beard?',
    yes: 'Yes — bearded.',
    no: 'No — clean-shaven.',
    test: (c) => c.portrait.beard !== 'none',
  },
  {
    id: 'headwear',
    group: 'look',
    ask: 'Are they wearing something on their head?',
    yes: 'Yes — their head is covered.',
    no: 'No — nothing on their head.',
    test: (c) => c.portrait.headwear !== 'none',
  },
  {
    id: 'robe-blue',
    group: 'look',
    ask: 'Is their robe blue or blue-grey?',
    yes: 'Yes — blue.',
    no: 'No — not a blue robe.',
    test: (c) => c.portrait.robe === 'indigo' || c.portrait.robe === 'slate',
  },
  {
    id: 'robe-red',
    group: 'look',
    ask: 'Is their robe red or rust-coloured?',
    yes: 'Yes — red or rust.',
    no: 'No — not a red robe.',
    test: (c) => c.portrait.robe === 'wine' || c.portrait.robe === 'clay',
  },
  {
    id: 'emblem-light',
    group: 'look',
    ask: 'Is there a light on their emblem — a flame, a lamp or a star?',
    yes: 'Yes — a light.',
    no: 'No light on the emblem.',
    test: emblemIn('fire', 'lamp', 'star'),
  },
  {
    id: 'emblem-scroll',
    group: 'look',
    ask: 'Is there a scroll on their emblem?',
    yes: 'Yes — a scroll.',
    no: 'No scroll.',
    test: emblemIn('scroll'),
  },
  {
    id: 'emblem-blade',
    group: 'look',
    ask: 'Is there a blade on their emblem?',
    yes: 'Yes — a blade.',
    no: 'No blade.',
    test: emblemIn('sword', 'saw'),
  },
  {
    id: 'emblem-staff',
    group: 'look',
    ask: 'Is there a staff on their emblem?',
    yes: 'Yes — a staff.',
    no: 'No staff.',
    test: emblemIn('staff'),
  },
  {
    id: 'emblem-vessel',
    group: 'look',
    ask: 'Is there something to carry or pour from on their emblem?',
    yes: 'Yes — a cup, a jar or a purse.',
    no: 'Nothing to carry or pour from.',
    test: emblemIn('chalice', 'water-jar', 'coin-purse'),
  },
  {
    id: 'emblem-bread',
    group: 'look',
    ask: 'Is there bread on their emblem?',
    yes: 'Yes — bread.',
    no: 'No bread.',
    test: emblemIn('bread'),
  },
  {
    id: 'emblem-creature',
    group: 'look',
    ask: 'Is there a living creature on their emblem?',
    yes: 'Yes — a creature.',
    no: 'No creature.',
    test: emblemIn('fish', 'lion'),
  },
  {
    id: 'emblem-cloth',
    group: 'look',
    ask: 'Is there cloth on their emblem — a cloak or a tent?',
    yes: 'Yes — cloth.',
    no: 'No cloth.',
    test: emblemIn('cloak', 'tent'),
  },
  {
    id: 'emblem-harp',
    group: 'look',
    ask: 'Is there a harp on their emblem?',
    yes: 'Yes — a harp.',
    no: 'No harp.',
    test: emblemIn('harp'),
  },

  /* ── the person behind it ─────────────────────────────────────────────── */
  {
    id: 'old',
    group: 'life',
    ask: 'Did they live before Jesus was born?',
    yes: 'Yes — Old Testament.',
    no: 'No — they belong to the New.',
    note:
      'Ten of the thirty are from the Old Testament; the rest are the Twelve and the church that came after them.',
    test: (c) => c.testament === 'old',
  },
  {
    id: 'woman',
    group: 'life',
    ask: 'Is this a woman?',
    yes: 'Yes — a woman.',
    no: 'No — a man.',
    note:
      'Seven women stand among these thirty: two Marys, Priscilla, Lydia, Ruth, Esther and Deborah.',
    test: (c) => WOMEN.has(c.id),
  },
  {
    id: 'twelve',
    group: 'life',
    ask: 'Were they one of the Twelve?',
    yes: 'Yes — one of the Twelve.',
    no: 'No — not one of the Twelve.',
    note: 'Matthew lists the Twelve by name, Judas Iscariot included.',
    ref: 'Matthew 10:2–4',
    test: (c) => TWELVE.has(c.id),
  },
  {
    id: 'met-jesus',
    group: 'life',
    ask: 'Did they meet Jesus during his life on earth?',
    yes: 'Yes — they stood in front of him.',
    no: 'No — not while he walked.',
    note:
      'The Twelve, his mother, Mary Magdalene and John the Baptist all appear beside him in the Gospels. Paul did not: he met the risen Christ on the Damascus road, years later.',
    ref: 'Acts 9:3–6',
    test: (c) => MET_JESUS.has(c.id),
  },
  {
    id: 'fisher',
    group: 'life',
    ask: 'Did they fish for a living?',
    yes: 'Yes — a fisherman.',
    no: 'No — not a fisherman.',
    note:
      'Jesus calls two pairs of brothers off their boats beside the Sea of Galilee: Simon and Andrew first, then James and John.',
    ref: 'Matthew 4:18–22',
    test: (c) => FISHERMEN.has(c.id),
  },
  {
    id: 'prophet',
    group: 'life',
    ask: 'Does scripture call them a prophet?',
    yes: 'Yes — a prophet.',
    no: 'No — never called one.',
    note:
      'The word covers more people than you would guess. God calls Abraham a prophet to Abimelech, Judges calls Deborah a prophetess, Jesus himself calls Daniel one — and at Pentecost Peter calls David one.',
    ref: 'Genesis 20:7 · Judges 4:4 · Matthew 24:15 · Acts 2:30',
    test: (c) => PROPHETS.has(c.id),
  },
  {
    id: 'crowned',
    group: 'life',
    ask: 'Did they wear a crown?',
    yes: 'Yes — crowned.',
    no: 'No crown.',
    note:
      'Only two: David, anointed king over Israel at Hebron, and Esther, crowned queen of Persia in Susa.',
    ref: '2 Samuel 5:3 · Esther 2:17',
    test: (c) => CROWNED.has(c.id),
  },
  {
    id: 'court',
    group: 'life',
    ask: 'Did they serve inside a foreign ruler’s household?',
    yes: 'Yes — inside a foreign court.',
    no: 'No — never at a foreign court.',
    note:
      'Joseph is set over Egypt, Daniel over the province of Babylon, Esther taken into the palace at Susa. All three end up speaking for their people from inside the empire that took them.',
    ref: 'Genesis 41:41 · Daniel 2:48 · Esther 2:17',
    test: (c) => FOREIGN_COURT.has(c.id),
  },
  {
    id: 'second-name',
    group: 'life',
    ask: 'Does scripture give them a second name?',
    yes: 'Yes — known by two names.',
    no: 'No — one name only.',
    note:
      'Simon is called Cephas, Abram becomes Abraham, Hadassah is Esther, Daniel is Belteshazzar, Joseph is Zaphenath-Paneah, Thomas is Didymus, Saul is also called Paul, and the apostles rename Joseph of Cyprus Barnabas.',
    ref: 'John 1:42 · Genesis 17:5 · Acts 13:9',
    test: (c) => SECOND_NAME.has(c.id),
  },
  {
    id: 'put-to-death',
    group: 'life',
    ask: 'Does the Bible record them being put to death?',
    yes: 'Yes — killed, in scripture.',
    no: 'No — scripture does not record it.',
    note:
      'Three, and only three: John the Baptist beheaded for Herodias, Stephen stoned outside the city, James killed with the sword by Herod. What happened to most of the others comes from later tradition, not from the Bible.',
    ref: 'Matthew 14:10 · Acts 7:59–60 · Acts 12:2',
    test: (c) => PUT_TO_DEATH.has(c.id),
  },
];

export const QUESTIONS_BY_ID = Object.fromEntries(
  FACE_QUESTIONS.map((q) => [q.id, q]),
);
